import {
  cloneElement,
  type CSSProperties,
  type FC,
  isValidElement,
  type JSX,
  type ReactElement,
  type Ref,
  useCallback,
  useId,
  useMemo,
} from 'react';

import { pick } from 'lodash-es';

import CircularProgress from '@mui/material/CircularProgress';
import { type Theme, useTheme } from '@mui/material/styles';

import { type QuantitativeSize } from '../../types/sizes';
import { evaluateHtmlColor } from '../../util/colors';
import { quantitativeSizeToString } from '../../util/sizes';
import { Box } from '../structural/Box';
import { Tooltip, type TooltipProps } from '../tooltips/Tooltip';

import * as types from './types';
import { getIconStyle } from './util';

const cloneAndOverrideElement = (
  theme: Theme,
  ele: ReactElement<types.IconProps>,
  {
    style,
    ...params
  }: { readonly isWrapped?: boolean; readonly style?: CSSProperties } & types.IconStyleParams,
  props: types.NativeIconProps,
) =>
  cloneElement(ele, {
    ...ele.props,
    ...props,
    style: getIconStyle(theme, {
      ...params,
      isCloning: true,
      style: { ...ele.props.style, ...style },
    }),
  });

interface IconContainerProps extends Pick<
  types.IconProps,
  'backgroundColor' | 'padding' | 'style' | keyof types.IconStyleParams
> {
  readonly children: JSX.Element;
  readonly ref?: Ref<HTMLElement>;
}

const IconContainer = ({
  backgroundColor = 'background.paper',
  children,
  color,
  dimension,
  padding = '4px',
  ref,
  size,
  ...props
}: IconContainerProps) => {
  const theme = useTheme();
  return (
    <Box
      {...props}
      ref={ref}
      style={{
        ...props.style,
        ...getIconStyle(theme, { color, dimension, size }),
        backgroundColor: evaluateHtmlColor(theme, backgroundColor),
        border: `1px solid ${evaluateHtmlColor(theme, backgroundColor)}`,
        borderRadius: '100%',
        padding: quantitativeSizeToString(padding, 'px'),
      }}
    >
      {children}
    </Box>
  );
};

interface IconLoadingStateProps extends Pick<
  types.IconProps,
  'style' | 'variant' | keyof types.IconStyleParams
> {
  /**
   * @default 2px
   */
  readonly loadingPadding?: QuantitativeSize;
}

const IconLoadingState: FC<IconLoadingStateProps> = ({
  loadingPadding = '2px',
  variant = types.IconVariant.Bare,
  ...props
}) => {
  const theme = useTheme();
  return (
    <Box
      style={{
        ...props.style,
        ...getIconStyle(theme, {
          ...props,
          isWrapped: variant === types.IconVariant.Contained,
        }),
        padding: quantitativeSizeToString(loadingPadding, 'px'),
      }}
    >
      <CircularProgress aria-label='Loading' color='inherit' size='100%' />
    </Box>
  );
};

interface IconInnerProps extends Omit<types.IconProps, 'children' | 'ref'> {
  readonly children?: ReactElement<types.IconProps>;
  readonly icon?: null | types.IconProp;
}

const IconInner = ({
  backgroundColor,
  children,
  color,
  dimension = types.DefaultIconDimension,
  icon: IconElement,
  maxHeight,
  maxWidth,
  minHeight,
  minWidth,
  padding,
  ref,
  size = types.DefaultIconSize,
  style,
  variant = types.IconVariant.Bare,
  ...props
}: { readonly ref?: Ref<HTMLDivElement & SVGSVGElement> } & IconInnerProps) => {
  const theme = useTheme();

  const iconStyleParams = useMemo<types.IconStyleParams>(
    () => ({
      color,
      dimension,
      maxHeight,
      maxWidth,
      minHeight,
      minWidth,
      size,
    }),
    [size, color, dimension, maxHeight, maxWidth, minHeight, minWidth],
  );

  const wrap = useCallback(
    (child: JSX.Element) => (
      <IconContainer
        {...iconStyleParams}
        backgroundColor={backgroundColor}
        padding={padding}
        ref={ref}
        style={style}
      >
        {child}
      </IconContainer>
    ),
    [iconStyleParams, backgroundColor, padding, ref, style],
  );

  if (IconElement && isValidElement<types.IconProps>(IconElement)) {
    if (variant === types.IconVariant.Contained) {
      return wrap(
        cloneAndOverrideElement(
          theme,
          IconElement,
          { ...iconStyleParams, isWrapped: true, style },
          props,
        ),
      );
    }
    return cloneAndOverrideElement(
      theme,
      IconElement,
      { ...iconStyleParams, style },
      { ...props, ref },
    );
  } else if (IconElement) {
    if (variant === types.IconVariant.Contained) {
      return wrap(
        <IconElement
          {...props}
          color='inherit'
          style={getIconStyle(theme, { dimension, isWrapped: true })}
        />,
      );
    }
    return (
      <IconElement
        {...props}
        ref={ref}
        style={getIconStyle(theme, { color, dimension, size, style })}
      />
    );
  } else if (children) {
    if (variant === types.IconVariant.Contained) {
      return wrap(
        cloneAndOverrideElement(
          theme,
          children,
          { ...iconStyleParams, isWrapped: true, style },
          props,
        ),
      );
    }
    return cloneAndOverrideElement(
      theme,
      children,
      { ...iconStyleParams, style },
      { ...props, ref },
    );
  }
  return null;
};

interface IconProps extends IconInnerProps, IconLoadingStateProps {
  readonly isLoading?: boolean;
  readonly isTooltipDisabled?: boolean;
  readonly loadingColor?: types.IconProps['color'];
  readonly tooltip?: TooltipProps['title'];
  readonly tooltipProps?: Omit<TooltipProps, 'children' | 'isDisabled' | 'title'>;
}

const LocalIcon: FC<IconProps> = ({
  isLoading = false,
  isTooltipDisabled,
  loadingColor,
  loadingPadding,
  tooltip,
  tooltipProps,
  ...props
}) => {
  const tooltipId = useId();

  if (isLoading) {
    if (props.variant === types.IconVariant.Contained) {
      return (
        <IconContainer
          {...pick(props, ['size', 'dimension', 'color', 'backgroundColor', 'padding', 'sx'])}
        >
          <IconLoadingState
            {...pick(props, ['size', 'dimension', 'color', 'variant'])}
            color={loadingColor ?? props.color}
            loadingPadding={loadingPadding}
          />
        </IconContainer>
      );
    }
    return (
      <IconLoadingState
        {...pick(props, ['size', 'dimension', 'variant', 'style'])}
        color={loadingColor ?? props.color}
        loadingPadding={loadingPadding}
      />
    );
  }

  if (tooltip) {
    return (
      <Tooltip
        {...tooltipProps}
        id={tooltipProps?.id ?? tooltipId}
        isDisabled={isTooltipDisabled}
        title={tooltip}
      >
        <IconInner
          aria-describedby={tooltipProps?.id ?? tooltipId}
          aria-hidden={false}
          cursor='pointer'
          tabIndex={0}
          {...props}
        />
      </Tooltip>
    );
  }
  return <IconInner {...props} />;
};

type IconVariantPartials = Record<types.IconVariant, FC<Omit<IconProps, 'variant'>>>;

export const Icon = Object.assign(
  LocalIcon,
  Object.values(types.IconVariant).reduce(
    (acc: IconVariantPartials, variant) => ({
      ...acc,
      [variant]: (props: Omit<IconProps, 'variant'>) => <LocalIcon {...props} variant={variant} />,
    }),
    {} as IconVariantPartials,
  ),
);
